import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./HistoryPage.css";
import { getHistory,deleteHistory } from "../services/getHistory";
import { ROUTES } from "../constants/routes";
import {
  Search,
  Plus,
  ChevronDown,
  FileText,
  ImageIcon,
  MoreHorizontal,
  X,
  Check,
  Edit2,
  Trash2
} from "lucide-react";

// Helper function to format dates/timestamps into "Today", "Yesterday", "2d ago", etc.
const formatTimeAgo = (rawTime, timestamp) => {
  if (!rawTime && !timestamp) return "Today";

  if (
    typeof rawTime === "string" &&
    (rawTime.includes("ago") || rawTime === "Today" || rawTime === "Yesterday")
  ) {
    return rawTime;
  }

  let dateObj = null;
  if (timestamp) {
    dateObj = new Date(timestamp);
  } else if (rawTime) {
    dateObj = new Date(rawTime);
  }

  if (!dateObj || isNaN(dateObj.getTime())) {
    return rawTime || "Today";
  }

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 20 && now.getDate() === dateObj.getDate()) {
    return "Today";
  } else if (diffDays === 1 || (diffHours < 48 && now.getDate() - dateObj.getDate() === 1)) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${Math.max(2, diffDays)}d ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}w ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months}m ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years}y ago`;
  }
};

const HistoryPage = () => {
  const navigate = useNavigate();
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("Newest");
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // State for active 3-dots action menu and inline editing
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // Close menus when clicking outside
  useEffect(() => {
    const handleOutsideClick = () => {
      setActiveMenuId(null);
      setSortDropdownOpen(false);
    };
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const response = await getHistory(1, 10);
        if (response && (Array.isArray(response) || typeof response === "object")) {
          let list = [];
          if (Array.isArray(response)) {
            list = response;
          } else {
            Object.values(response).forEach((arr) => {
              if (Array.isArray(arr)) {
                list = [...list, ...arr];
              }
            });
          }

          if (list.length > 0) {
            const formatted = list.map((item, index) => {
              const isEval =
                item.template === "evaluate" ||
                item.prompt === "evaluate" ||
                item.prompt_type === "evaluate" ||
                Boolean(item.file_name || item.attached_file || item.attachment || item.fileName);

              const fileName =
                item.file_name ||
                item.attached_file ||
                item.attachment ||
                item.fileName ||
                item.file_detail?.name ||
                (isEval ? "evaluated_document.pdf" : null);

              return {
                conversation_id: item.conversation_id || item.id || `session-${index}`,
                message: item.message || item.prompt || item.title || "Untitled Chat",
                timeAgo: formatTimeAgo(item.created_at || item.time_ago, item.timestamp),
                template: isEval ? "evaluate" : item.template,
                attachedFile: fileName,
                fileType:
                  item.file_type ||
                  (fileName && (fileName.endsWith(".jpg") || fileName.endsWith(".png") || fileName.endsWith(".jpeg"))
                    ? "image"
                    : "document"),
                timestamp: item.timestamp || Date.now() - index * 3600000
              };
            });
            setHistoryItems(formatted);
            setHasMore(list.length >= 10);
          } else {
            setHistoryItems([]);
            setHasMore(false);
          }
        } else {
          setHistoryItems([]);
          setHasMore(false);
        }
      } catch (err) {
        console.error("Failed to load history:", err);
        setHistoryItems([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  // Toggle selection for individual session
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((i) => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  // Toggle select all sessions
  const handleSelectAllToggle = (e) => {
    e.stopPropagation();
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      setSelectedItems(historyItems.map((item) => item.conversation_id));
      setSelectAll(true);
    }
  };

  // Start inline rename
  const handleStartRename = (item, e) => {
    e.stopPropagation();
    setActiveMenuId(null);
    setEditingId(item.conversation_id);
    setEditingText(item.message);
  };

  // Save inline rename
  const handleSaveRename = (id, e) => {
    if (e) e.stopPropagation();
    if (!editingText.trim()) return;
    setHistoryItems((prev) =>
      prev.map((item) =>
        item.conversation_id === id ? { ...item, message: editingText.trim() } : item
      )
    );
    setEditingId(null);
    setEditingText("");
  };

  // Delete session item
  const handleDeleteSession = async (id, e) => {
    e.stopPropagation();
    setActiveMenuId(null);
    try {
      await deleteHistory(id);
      setHistoryItems((prev) => prev.filter((item) => item.conversation_id !== id));
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    } catch (error) {
      console.error("Failed to delete history:", error);
      alert("Failed to delete conversation. Please try again.");
    }
  };

  // Load more history items (infinite scroll)
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const response = await getHistory(nextPage, 10);
      if (response && (Array.isArray(response) || typeof response === "object")) {
        let list = [];
        if (Array.isArray(response)) {
          list = response;
        } else {
          Object.values(response).forEach((arr) => {
            if (Array.isArray(arr)) {
              list = [...list, ...arr];
            }
          });
        }

        if (list.length > 0) {
          const formatted = list.map((item, index) => {
            const isEval =
              item.template === "evaluate" ||
              item.prompt === "evaluate" ||
              item.prompt_type === "evaluate" ||
              Boolean(item.file_name || item.attached_file || item.attachment || item.fileName);

            const fileName =
              item.file_name ||
              item.attached_file ||
              item.attachment ||
              item.fileName ||
              item.file_detail?.name ||
              (isEval ? "evaluated_document.pdf" : null);

            return {
              conversation_id: item.conversation_id || item.id || `session-${historyItems.length + index}`,
              message: item.message || item.prompt || item.title || "Untitled Chat",
              timeAgo: formatTimeAgo(item.created_at || item.time_ago, item.timestamp),
              template: isEval ? "evaluate" : item.template,
              attachedFile: fileName,
              fileType:
                item.file_type ||
                (fileName && (fileName.endsWith(".jpg") || fileName.endsWith(".png") || fileName.endsWith(".jpeg"))
                  ? "image"
                  : "document"),
              timestamp: item.timestamp || Date.now() - (historyItems.length + index) * 3600000
            };
          });
          setHistoryItems((prev) => [...prev, ...formatted]);
          setPage(nextPage);
          setHasMore(list.length >= 10);
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Failed to load more history:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // Filter items based on search query
  const filteredItems = historyItems.filter((item) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    const msgMatch = item.message.toLowerCase().includes(query);
    const fileMatch = item.attachedFile ? item.attachedFile.toLowerCase().includes(query) : false;
    return msgMatch || fileMatch;
  });

  // Sort items based on selection
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortBy === "Newest") {
      return (b.timestamp || 0) - (a.timestamp || 0);
    } else if (sortBy === "Oldest") {
      return (a.timestamp || 0) - (b.timestamp || 0);
    } else if (sortBy === "Title") {
      return a.message.localeCompare(b.message);
    }
    return 0;
  });

  return (
    <div className="history-page-wrapper">
      {/* Top Header Bar */}
      <div className="history-page-header">
        <div className="history-header-actions history-actions-left">
          {/* Checkbox Select Pill */}
          <div className="history-select-dropdown" onClick={handleSelectAllToggle}>
            <div className={`history-checkbox-icon ${selectAll ? "checked" : ""}`}>
              {selectAll && <Check size={11} />}
            </div>
          </div>

          {/* Sort Selector Pill */}
          <div
            className="history-sort-dropdown"
            onClick={(e) => {
              e.stopPropagation();
              setSortDropdownOpen(!sortDropdownOpen);
            }}
          >
            <span>Sort: {sortBy}</span>
            <ChevronDown size={13} />

            {sortDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "38px",
                  left: 0,
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  zIndex: 20,
                  width: "130px",
                  overflow: "hidden"
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {["Newest", "Oldest", "Title"].map((opt) => (
                  <div
                    key={opt}
                    style={{
                      padding: "8px 12px",
                      fontSize: "12.5px",
                      cursor: "pointer",
                      backgroundColor: sortBy === opt ? "#f1f5f9" : "transparent",
                      fontWeight: sortBy === opt ? "600" : "400",
                      color: sortBy === opt ? "#0298da" : "#334155"
                    }}
                    onClick={() => {
                      setSortBy(opt);
                      setSortDropdownOpen(false);
                    }}
                  >
                    Sort: {opt}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="history-header-actions history-actions-right">
          {/* Search Trigger Icon / Expanded Search Input */}
          <div className="history-search-container">
            {!searchOpen ? (
              <button
                className="history-search-btn"
                onClick={() => setSearchOpen(true)}
                title="Search history"
              >
                <Search size={17} />
              </button>
            ) : (
              <div className="history-search-input-wrapper">
                <Search size={15} className="history-search-icon-inside" />
                <input
                  type="text"
                  className="history-search-input"
                  placeholder="Search in history..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <button
                  className="history-search-clear"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchOpen(false);
                  }}
                >
                  <X size={15} />
                </button>
              </div>
            )}
          </div>

          {/* + New Chat Button */}
          <button
            className="history-new-chat-btn"
            onClick={() =>
              navigate(ROUTES.CHAT, {
                state: { flag: Math.floor(Math.random() * 1000000) },
              })
            }
          >
            <Plus size={16} />
            <span>New</span>
          </button>
        </div>
      </div>

      {/* Session Items List */}
      <div className="history-list-container">
        {loading ? (
          <div className="history-skeleton-list" aria-label="Loading history" role="status">
            {Array.from({ length: 6 }).map((_, index) => (
              <div className="history-skeleton-row" key={index}>
                <span className="history-skeleton-icon" />
                <span className="history-skeleton-text" />
                <span className="history-skeleton-time" />
              </div>
            ))}
          </div>
        ) : sortedItems.length === 0 ? (
          <div className="history-empty-state">
            {searchQuery.trim() ? "No matching history found." : "No history yet. Start a new chat to see your conversations here."}
          </div>
        ) : (
          sortedItems.map((item) => {
            const isSelected = selectedItems.includes(item.conversation_id);
            const isEditing = editingId === item.conversation_id;
            const isEvaluateTemplate =
              item.template === "evaluate" ||
              item.prompt === "evaluate" ||
              item.prompt_type === "evaluate" ||
              Boolean(item.attachedFile);
            
            const displayFileName = item.attachedFile || (isEvaluateTemplate ? "evaluated_document.pdf" : null);
            const isMenuOpen = activeMenuId === item.conversation_id;

            return (
              <div
                key={item.conversation_id}
                className={`history-item-row ${isEditing ? "editing" : ""}`}
                onClick={() => {
                  if (!isEditing) {
                    navigate(`${ROUTES.CHAT || "/chat"}/${item.conversation_id}`);
                  }
                }}
              >
                <div className="history-item-left">
                  {/* Search / Selection Icon */}
                  <div
                    onClick={(e) => toggleSelect(item.conversation_id, e)}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {isSelected ? (
                      <div className="history-checkbox-icon checked">
                        <Check size={11} />
                      </div>
                    ) : (
                      <Search size={16} className="history-item-search-icon" />
                    )}
                  </div>

                  {/* Session Query Message / Inline Edit */}
                  {isEditing ? (
                    <div
                      className="history-inline-edit-wrapper"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        className="history-inline-edit-input"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveRename(item.conversation_id, e);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                      />
                      <button
                        className="history-inline-btn save"
                        onClick={(e) => handleSaveRename(item.conversation_id, e)}
                        title="Save"
                      >
                        <Check size={15} />
                      </button>
                      <button
                        className="history-inline-btn cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(null);
                        }}
                        title="Cancel"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <span className="history-item-prompt" title={item.message}>
                      {item.message}
                    </span>
                  )}

                  {/* Template 'evaluate' Attached File Pill */}
                  {!isEditing && isEvaluateTemplate && displayFileName && (
                    <div className="history-file-pill" title={displayFileName}>
                      {item.fileType === "image" ? (
                        <ImageIcon size={13} className="history-file-pill-icon" />
                      ) : (
                        <FileText size={13} className="history-file-pill-icon" />
                      )}
                      <span>{displayFileName}</span>
                    </div>
                  )}
                </div>

                <div className="history-item-right">
                  {/* Time ago badge */}
                  <span className="history-item-time">
                    {formatTimeAgo(item.timeAgo, item.timestamp)}
                  </span>

                  {/* More options 3-dots */}
                  <div style={{ position: "relative" }}>
                    <button
                      className={`history-item-more-btn ${isMenuOpen ? "active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenuId(isMenuOpen ? null : item.conversation_id);
                      }}
                      title="Options"
                    >
                      <MoreHorizontal size={17} />
                    </button>

                    {/* 3-Dots Action Menu Dropdown (Rename & Delete only) */}
                    {isMenuOpen && (
                      <div
                        className="history-action-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* <div
                          className="history-action-menu-item"
                          onClick={(e) => handleStartRename(item, e)}
                        >
                          <Edit2 size={14} />
                          <span>Rename</span>
                        </div> */}
                        <div
                          className="history-action-menu-item delete"
                          onClick={(e) => handleDeleteSession(item.conversation_id, e)}
                        >
                          <Trash2 size={14} />
                          <span>Delete</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        {hasMore && (
          <div className="history-load-more-container">
            <button
              className="history-load-more-btn"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
